import BaseModel from '../models/base-model';
import IMPSBaseModel from '../models/imps-base-model';

export const shouldUpdateState = <T>(
    data: Undefinable<MixType<T>>,
    loadingKey: Undefinable<string>
) => loadingKey || (Array.isArray(data) ? data.length > 0 : !!data);

export const storeData = <T>(
    data: Undefinable<MixType<T>>,
    key: keyof T | (keyof T)[]
): Dictionary<T> => {
    if (data) {
        if (Array.isArray(data)) {
            return data.reduce(
                (prev, x) => {
                    const mergeId = getMergeId(x, key);
                    if (mergeId) {
                        prev[mergeId] = x;
                    }
                    return prev;
                },
                {} as Dictionary<T>
            );
        } else {
            const mergeId = getMergeId(data, key);
            if (mergeId) {
                return {
                    [mergeId]: data
                } as Dictionary<T>;
            }
        }
    }
    return {} as Dictionary<T>;
};

export const storeById = <T extends BaseModel>(data: Undefinable<MixType<T>>): Dictionary<T> => {
    if (data) {
        if (Array.isArray(data)) {
            return data.reduce(
                (prev, x) => {
                    prev[x.id] = x;
                    return prev;
                },
                {} as Dictionary<T>
            );
        } else {
            return {
                [data.id]: data
            } as Dictionary<T>;
        }
    } else {
        return {} as Dictionary<T>;
    }
};

export const storeByIdConvert = <T extends BaseModel, K>(data: Undefinable<MixType<T>>, convertFunc: (param: T) => K): Dictionary<K> => {
    if (data) {
        if (Array.isArray(data)) {
            return data.reduce(
                (prev, x) => {
                    prev[x.id] = convertFunc(x);
                    return prev;
                },
                {} as Dictionary<K>
            );
        } else {
            return {
                [data.id]: convertFunc(data)
            } as Dictionary<K>;
        }
    } else {
        return {} as Dictionary<K>;
    }
};

export const storeByIMPSId = <T extends IMPSBaseModel | { impsId: string; }>(data: Undefinable<MixType<T>>): Dictionary<T> => {
    if (data) {
        if (Array.isArray(data)) {
            return data.reduce(
                (prev, x) => {
                    prev[x.impsId] = x;
                    return prev;
                },
                {} as Dictionary<T>
            );
        } else {
            return {
                [data.impsId]: data
            } as Dictionary<T>;
        }
    } else {
        return {} as Dictionary<T>;
    }
};

export const JsonParse = <T = Object>(data: Undefinable<string> | null): T => {
    return JSON.parse(data || '{}') as T;
};

const getMergeId = <T>(
    data: T,
    key: keyof T | (keyof T)[]
): string => {
    if (Array.isArray(key)) {
        return key.map(x => data[x] || '').join('---');
    } else {
        return String(data[key] || '');
    }
};
